import type { ApiErrorBody, ConfigResponse, SearchParams, SearchResponse } from './types'

export type ErrorCode =
    | 'INVALID_PARAMETER'
    | 'UNKNOWN_CONTEXT'
    | 'METHOD_NOT_ALLOWED'
    | 'NOT_FOUND'
    | 'INTERNAL_ERROR'
    | 'RATE_LIMITED'
    | 'NETWORK'
    | 'INVALID_RESPONSE'
    | 'SEARCH_FAILED'

/** Seconds to wait after a 429 without a usable `Retry-After`. */
export const DEFAULT_RETRY_AFTER = 60

/** Longest cooldown after a 429; `Retry-After` of the hourly total can be up to 3600 seconds. */
export const MAX_COOLDOWN = 60

/** Rate limit groups of the API: `/search`, `/summary` and `/conversation` share `search`, `/suggest` has its own. */
export type RateLimitGroup = 'search' | 'suggest'

export class AgentSearchError extends Error {
    readonly code: ErrorCode | string
    readonly status: number
    /** Seconds from `Retry-After` for `RATE_LIMITED`, `DEFAULT_RETRY_AFTER` when missing or not a number. */
    readonly retryAfter: number | null

    constructor(code: ErrorCode | string, status: number, message: string, retryAfter: number | null = null) {
        super(message)
        this.name = 'AgentSearchError'
        this.code = code
        this.status = status
        this.retryAfter = retryAfter
    }
}

export interface ClientOptions {
    /** Base path of the API, e.g. `/api/agent-search`. */
    endpoint: string
    /** Id of the search document. */
    context: string | number
    /** Fetch implementation, defaults to the global one. */
    fetch?: typeof fetch
}

export interface AgentSearchClient {
    config(signal?: AbortSignal): Promise<ConfigResponse>
    /** Rejects with `RATE_LIMITED` without a request while the group `search` cools down. */
    search(params: SearchParams, signal?: AbortSignal): Promise<SearchResponse>
    /**
     * True while a group cools down after a 429: until now + min(Retry-After, MAX_COOLDOWN). Summary, conversation and
     * suggestions are hidden and send nothing meanwhile.
     */
    isRateLimited(group: RateLimitGroup): boolean
}

export function createClient(options: ClientOptions): AgentSearchClient {
    const base = options.endpoint.replace(/\/+$/, '')
    const doFetch: typeof fetch = options.fetch ?? ((input, init) => fetch(input, init))
    // end of the cooldown per group (ms timestamp), per client instance
    const cooldownUntil: Partial<Record<RateLimitGroup, number>> = {}

    function isRateLimited(group: RateLimitGroup): boolean {
        return Date.now() < (cooldownUntil[group] ?? 0)
    }

    function url(path: string, params: Record<string, string>): string {
        const query = new URLSearchParams({ context: String(options.context), ...params })

        return `${base}/${path}?${query.toString()}`
    }

    async function request<T>(
        target: string,
        signal?: AbortSignal,
        isValid?: (body: unknown) => boolean,
        group?: RateLimitGroup,
    ): Promise<T> {
        if (group && isRateLimited(group)) {
            const seconds = Math.ceil(((cooldownUntil[group] ?? 0) - Date.now()) / 1000)

            throw new AgentSearchError('RATE_LIMITED', 429, `rate limited, ${group} cools down`, seconds)
        }

        let response: Response
        let body: unknown

        try {
            response = await doFetch(target, { headers: { Accept: 'application/json' }, signal })

            // by status, whatever the body: a proxy may answer 429 with HTML
            if (response.status === 429) {
                const retryAfter = parseRetryAfter(response.headers.get('Retry-After'))

                if (group) {
                    cooldownUntil[group] = Date.now() + Math.min(retryAfter, MAX_COOLDOWN) * 1000
                }

                throw new AgentSearchError('RATE_LIMITED', 429, 'rate limited', retryAfter)
            }

            body = await parseJson(response)
        } catch (error) {
            if (isAbortError(error) || error instanceof AgentSearchError) {
                throw error
            }

            throw new AgentSearchError('NETWORK', 0, error instanceof Error ? error.message : 'network error')
        }

        if (!response.ok) {
            const apiError = (body as Partial<ApiErrorBody>).error

            throw new AgentSearchError(
                apiError?.code ?? 'INTERNAL_ERROR',
                response.status,
                apiError?.message ?? `request failed with status ${response.status}`,
            )
        }

        if (isValid && !isValid(body)) {
            throw new AgentSearchError('INVALID_RESPONSE', response.status, 'unexpected response body')
        }

        return body as T
    }

    return {
        config: (signal) => request<ConfigResponse>(url('config', {}), signal),
        search: ({ q, page }, signal) =>
            request<SearchResponse>(url('search', page > 1 ? { q, page: String(page) } : { q }), signal, isSearchResponse, 'search'),
        isRateLimited,
    }
}

/** Integer seconds of `Retry-After`; DEFAULT_RETRY_AFTER when missing or not a number (e.g. an HTTP date). */
export function parseRetryAfter(value: string | null): number {
    const seconds = value !== null && /^\s*\d+\s*$/.test(value) ? Number.parseInt(value, 10) : Number.NaN

    return Number.isFinite(seconds) ? seconds : DEFAULT_RETRY_AFTER
}

async function parseJson(response: Response): Promise<unknown> {
    try {
        return await response.json()
    } catch (error) {
        if (isAbortError(error)) {
            throw error
        }

        throw new AgentSearchError('NETWORK', response.status, 'response is not JSON')
    }
}

/** Duck-typed on purpose: a DOMException from another realm (iframe, jsdom) is not `instanceof Error`. */
export function isAbortError(error: unknown): boolean {
    return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError'
}

/** The parts of a search response the component relies on. */
function isSearchResponse(body: unknown): boolean {
    return typeof body === 'object' && body !== null && Array.isArray((body as { blocks?: unknown }).blocks)
}
